import { Component, EventEmitter, inject, InjectFlags, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthApiService, MeApiService } from '@digilize/shared/data/services/src';
import { UserTerms } from '@digilize/shared/definitions/src/lib/interfaces/UserTerms';
import { AuthService } from '@digilize/shared/feature/modules/auth/src';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ManagePasswordComponent } from '../../../components/manage-password/manage-password.component';
import { MeProfileService } from '../../../services/me-profile.service';
import { WizardService } from '../../../services/wizard.service';

@Component({
  selector: 'bwc-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss'],
})
export class CreateAccountComponent implements OnInit {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @Output() emitClose = new EventEmitter();

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  submitting = false;
  submitError = false;
  submitErrorMessage = '';
  emailExists = false;
  hidePwd = true;
  hideConfirmPwd = true;
  private me: any;

  constructor(
    private fb: FormBuilder,
    private meApiService: MeApiService,
    private authApiService: AuthApiService,
    private authService: AuthService,
    private meProfileService: MeProfileService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        first_name: ['', [Validators.required]],
        last_name: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        password_confirm: ['', [Validators.required]],
        profile_visible: [false],
        terms: [false, [Validators.requiredTrue]],
      },
      { validators: this.passwordsMatchValidator }
    );

    this.meApiService.getMe().subscribe((me: any) => {
      if (me) {
        this.me = me;
        this.form.patchValue({
          first_name: me.first_name || '',
          last_name: me.last_name || '',
          email: me.email || '',
        });
      }
    });
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.submitting) {
      return;
    }

    this.submitting = true;
    this.submitError = false;
    this.emailExists = false;

    const formValue = this.form.value;

    this.meProfileService
      .postProfileWithAdress({
        first_name: formValue.first_name,
        last_name: formValue.last_name,
        email: formValue.email,
        phone: this.me?.phone ?? null,
        nickname: this.me?.nickname ?? null,
        description: this.me?.description ?? null,
        gender: this.me?.gender ?? null,
        birthday: this.me?.birthday ?? null,
        post_code: this.me?.post_code ?? null,
        city: this.me?.city ?? null,
        country: this.me?.country ?? null,
        street: this.me?.street ?? null,
        house_number: this.me?.house_number ?? null,
        street_number: this.me?.street_number ?? null,
        photo_id: this.me?.photo_id ?? null,
      })
      .pipe(
        switchMap(() => {
          const terms: UserTerms = {
            agreement1: true,
            agreement2: formValue.terms,
            agreement3: false,
          };

          return this.meApiService.updateTerms(terms);
        }),
        switchMap(() => {
          if (formValue.profile_visible) {
            return this.meApiService.updateVisibility({ visible_to_employers: true });
          }
          return of(null);
        }),
        switchMap(() =>
          this.authApiService.activateWithCredentials({
            email: formValue.email,
            password: formValue.password,
          })
        )
      )
      .subscribe({
        next: () => {
          this.submitting = false;

          this.openActivationPopup();
        },
        error: (error) => {
          this.submitting = false;
          if (error?.error?.error?.type === 'already_exists' || error?.error?.error?.info?.kind === 'already_exists') {
            this.emailExists = true;
          } else {
            this.submitError = true;
          }
        },
      });
  }

  private openActivationPopup(): void {
    this.getValidToken$().subscribe({
      next: () => {
        const dialogRef = this.dialog.open(ManagePasswordComponent, {
          disableClose: false,
          width: '670px',
          data: {
            type: 'create',
            password_created: true,
            email: this.form.value.email,
            checkYourEmail: true,
            noResendCode: true,
          },
        });
        this.submitting = false;

        dialogRef.afterClosed().subscribe((result) => {
          if (result?.activated) {
            if (this.wizardService) {
              this.wizardService.goToNext();
            } else {
              this.emitClose.emit('create_account');
            }
          }
        });
      },
      error: (err) => {
        console.error('Error sending activation code:', err);
        this.submitting = false;
      },
    });
  }

  continueWithoutAccount(): void {
    this.wizardService?.goToNext();
  }

  goPrev(): void {
    if (this.wizardService && this.wizardService.currentStep > 0) {
      this.wizardService.goToPrevious();
    } else {
      this.close();
    }
  }

  close(): void {
    this.emitClose.emit('create_account');
  }

  private passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirm = group.get('password_confirm')?.value;
    return password === confirm ? null : { passwordsMismatch: true };
  }

  private getValidToken$(): Observable<string> {
    const token = this.authService.getAccessToken();
    if (token) {
      return of(token);
    }
    return this.authService.refreshAccessToken().pipe(
      map(() => {
        const refreshedToken = this.authService.getAccessToken();
        if (!refreshedToken) {
          throw new Error('Unable to obtain access token');
        }
        return refreshedToken;
      })
    );
  }
}
